/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as VoiceSettingsActionCreators from '@app/actions/VoiceSettingsActionCreators';
import {Switch} from '@app/components/form/Switch';
import {AudioLevelMeter} from '@app/components/modals/tabs/components/AudioLevelMeter';
import styles from '@app/components/modals/tabs/components/MicTestSection.module.css';
import type {MicTestSettings} from '@app/components/modals/tabs/hooks/useMicTest';
import {useMicTest} from '@app/components/modals/tabs/hooks/useMicTest';
import {Button} from '@app/components/uikit/button/Button';
import {Slider} from '@app/components/uikit/Slider';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';

interface MicTestSectionProps {
	settings: MicTestSettings;
}

export const MicTestSection: React.FC<MicTestSectionProps> = observer(({settings}) => {
	const {t} = useLingui();
	const {isTesting, level, start, stop} = useMicTest(settings);
	const autoSensitivity = VoiceSettingsStore.vadAutoSensitivity;
	const threshold = VoiceSettingsStore.vadThreshold;
	const [dragThreshold, setDragThreshold] = useState<number | null>(null);

	const activeThreshold = dragThreshold ?? threshold;

	const handleAutoSensitivityChange = useCallback((value: boolean) => {
		VoiceSettingsActionCreators.update({vadAutoSensitivity: value});
	}, []);

	const handleThresholdChange = useCallback((value: number) => {
		setDragThreshold(null);
		VoiceSettingsActionCreators.update({vadThreshold: value});
	}, []);

	const handleThresholdDrag = useCallback((value: number) => {
		setDragThreshold(value);
	}, []);

	return (
		<div>
			<div className={styles.label}>
				<Trans>Mic Test</Trans>
			</div>
			<div className={styles.content}>
				<AudioLevelMeter
					level={isTesting ? level : 0}
					thresholdPercent={activeThreshold}
					showThreshold={isTesting && !autoSensitivity}
				/>
				<div className={styles.controls}>
					<Button
						variant="primary"
						fitContainer={false}
						className={styles.actionButton}
						onClick={isTesting ? stop : start}
					>
						{isTesting ? <Trans>Stop Test</Trans> : <Trans>Test Microphone</Trans>}
					</Button>
					{isTesting && (
						<span className={styles.listening}>
							<Trans>Listening...</Trans>
						</span>
					)}
				</div>

				<div className={styles.sensitivitySection}>
					<div className={styles.sensitivityHeader}>
						<Trans>Input Sensitivity</Trans>
					</div>

					<Switch
						label={<Trans>Automatically Determine Input Sensitivity</Trans>}
						value={autoSensitivity}
						onChange={handleAutoSensitivityChange}
						ariaLabel={t`Detect input sensitivity automatically`}
					/>

					<div
						className={styles.sliderSection}
						style={{opacity: autoSensitivity ? 0.4 : 1, pointerEvents: autoSensitivity ? 'none' : undefined}}
					>
						<Slider
							value={threshold}
							defaultValue={threshold}
							factoryDefaultValue={50}
							minValue={0}
							maxValue={100}
							onValueRender={(value) => `${Math.round(value)}%`}
							onValueChange={handleThresholdChange}
							asValueChanges={handleThresholdDrag}
							disabled={autoSensitivity}
						/>
					</div>
				</div>
			</div>
		</div>
	);
});
